import { useMemo, useState } from "react";
import { Building2, CalendarRange, DollarSign, Download, Truck } from "lucide-react";
import { Button } from "../ui/button";
import { RESTAURANT_BY_ID } from "../../constants";
import { buildCsvFilename, downloadCsv } from "../../lib/csv";
import { toast } from "sonner";

// Money formatter — one place so we can swap locale later if needed.
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

// Build every confirmed line-item across requests with its computed spend
// (receivedQty × costPerUnit). Transfers are excluded — no vendor spend.
function collectConfirmedLines(requests, productsById) {
  const rows = [];
  (requests || []).forEach((r) => {
    if (r.type !== "purchase") return;
    if (!r.deliveryConfirmedAt) return;
    (r.items || []).forEach((line) => {
      const product = productsById.get(line.productId);
      const received = Number(line.receivedQty ?? 0);
      const cost = Number(product?.costPerUnit ?? 0);
      const spend = received * cost;
      rows.push({
        request: r,
        line,
        product,
        received,
        unitCost: cost,
        spend,
      });
    });
  });
  return rows;
}

function sumBy(rows, keyFn) {
  const acc = new Map();
  rows.forEach((row) => {
    const k = keyFn(row);
    if (k == null) return;
    const prev = acc.get(k) || { total: 0, lines: 0, units: 0 };
    acc.set(k, {
      total: prev.total + row.spend,
      lines: prev.lines + 1,
      units: prev.units + row.received,
    });
  });
  return acc;
}

function yearMonth(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function FinanceDashboard({ user, requests, products, suppliers }) {
  const [range, setRange] = useState("all"); // all | 30d | 90d | ytd

  const productsById = useMemo(() => new Map((products || []).map((p) => [p.id, p])), [products]);
  const suppliersById = useMemo(() => new Map((suppliers || []).map((s) => [s.id, s])), [suppliers]);

  const filteredRequests = useMemo(() => {
    const now = Date.now();
    let cutoff = 0;
    if (range === "30d") cutoff = now - 30 * 24 * 3600 * 1000;
    else if (range === "90d") cutoff = now - 90 * 24 * 3600 * 1000;
    else if (range === "ytd") cutoff = new Date(new Date().getFullYear(), 0, 1).getTime();
    if (!cutoff) return requests;
    return requests.filter((r) => {
      const t = new Date(r.deliveryConfirmedAt || r.createdAt).getTime();
      return t >= cutoff;
    });
  }, [requests, range]);

  const rows = useMemo(
    () => collectConfirmedLines(filteredRequests, productsById),
    [filteredRequests, productsById]
  );

  const totalSpend = rows.reduce((s, r) => s + r.spend, 0);
  const uncostedLines = rows.filter((r) => r.unitCost === 0).length;

  const byVendor = useMemo(() => {
    const m = sumBy(rows, (r) => r.request.vendor);
    const arr = Array.from(m.entries()).map(([vendorId, v]) => ({
      vendorId,
      vendor: suppliersById.get(vendorId),
      ...v,
    }));
    arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [rows, suppliersById]);

  const byMonth = useMemo(() => {
    const m = sumBy(rows, (r) => yearMonth(r.request.deliveryConfirmedAt || r.request.createdAt));
    const arr = Array.from(m.entries()).map(([month, v]) => ({ month, ...v }));
    arr.sort((a, b) => (a.month > b.month ? -1 : 1));
    return arr;
  }, [rows]);

  const byRestaurant = useMemo(() => {
    const m = sumBy(rows, (r) => r.request.restaurant);
    const arr = Array.from(m.entries()).map(([rid, v]) => ({ rid, ...v }));
    arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [rows]);

  const monthMax = byMonth.reduce((m, x) => Math.max(m, x.total), 0);

  const exportCsv = () => {
    if (rows.length === 0) return toast.error("No confirmed spend in this range");
    const header = [
      "Confirmed at", "Restaurant", "Vendor", "Invoice #",
      "Product", "Category", "Qty received", "Unit", "Unit cost", "Spend", "Request ID",
    ];
    const data = rows.map((row) => [
      row.request.deliveryConfirmedAt || "",
      RESTAURANT_BY_ID[row.request.restaurant]?.name || "",
      suppliersById.get(row.request.vendor)?.name || "",
      row.request.invoiceNumber || "",
      row.product?.name || row.line.productName || "",
      row.product?.category || "",
      row.received,
      row.line.unit || row.product?.unit || "",
      row.unitCost,
      row.spend,
      row.request.id,
    ]);
    downloadCsv(buildCsvFilename("finance"), [header, ...data]);
    toast.success(`Exported ${rows.length} line${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <section data-testid="finance-dashboard">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="inline-flex rounded-lg bg-white border border-[#E6E0D4] p-1" role="tablist">
          {[
            { v: "30d", l: "Last 30 days" },
            { v: "90d", l: "Last 90 days" },
            { v: "ytd", l: "Year to date" },
            { v: "all", l: "All time" },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`finance-range-${o.v}`}
              onClick={() => setRange(o.v)}
              className={`h-9 px-3.5 rounded-md text-sm transition-colors ${
                range === o.v ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/75 hover:bg-[#F3EFE7]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button
          data-testid="finance-export-btn"
          variant="outline"
          size="sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="border-[#E6E0D4] bg-white hover:bg-[#F3EFE7] text-[#0B1F3A]"
        >
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KPI icon={<DollarSign size={18} />} label="Total spend" value={money(totalSpend)} accent="text-[#0B1F3A]" testId="kpi-total" />
        <KPI icon={<Truck size={18} />} label="Vendors used" value={byVendor.length} accent="text-[#0B1F3A]" testId="kpi-vendors" />
        <KPI icon={<CalendarRange size={18} />} label="Confirmed lines" value={rows.length} accent="text-[#0B1F3A]" testId="kpi-lines" />
        <KPI icon={<Building2 size={18} />} label="Restaurants" value={byRestaurant.length} accent="text-[#0B1F3A]" testId="kpi-restaurants" />
      </div>

      {uncostedLines > 0 && (
        <div className="mb-5 bg-[#FDEFCF] border border-[#D48E36]/40 text-[#A56614] rounded-xl px-4 py-3 text-sm">
          <strong>{uncostedLines}</strong> line{uncostedLines === 1 ? "" : "s"} had no <em>cost per unit</em> set, so they're counted as $0. Edit those products in the catalog to improve accuracy.
        </div>
      )}

      {/* By vendor */}
      <Section title="By vendor" count={byVendor.length} empty="No confirmed deliveries in this range.">
        {byVendor.map((v) => (
          <Row
            key={v.vendorId || "none"}
            testId={`vendor-row-${v.vendorId}`}
            title={v.vendor?.name || "—"}
            subtitle={`${v.vendor?.type === "delivered" ? "Delivered" : "Direct"} · ${v.lines} line${v.lines === 1 ? "" : "s"} · ${v.units} units`}
            value={money(v.total)}
            sharePct={totalSpend > 0 ? Math.round((v.total / totalSpend) * 100) : 0}
          />
        ))}
      </Section>

      {/* By restaurant */}
      <Section title="By restaurant" count={byRestaurant.length} empty="—">
        {byRestaurant.map((r) => (
          <Row
            key={r.rid}
            testId={`restaurant-row-${r.rid}`}
            title={RESTAURANT_BY_ID[r.rid]?.name || r.rid}
            subtitle={`${r.lines} line${r.lines === 1 ? "" : "s"} · ${r.units} units`}
            value={money(r.total)}
            sharePct={totalSpend > 0 ? Math.round((r.total / totalSpend) * 100) : 0}
          />
        ))}
      </Section>

      {/* By month */}
      <Section title="By month" count={byMonth.length} empty="—">
        {byMonth.map((m) => {
          const barPct = monthMax > 0 ? Math.round((m.total / monthMax) * 100) : 0;
          return (
            <div key={m.month} className="px-4 md:px-5 py-3" data-testid={`month-row-${m.month}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[#0B1F3A]">{m.month}</p>
                <p className="text-sm font-medium text-[#0B1F3A]">{money(m.total)}</p>
              </div>
              <div className="mt-2 h-1.5 w-full bg-[#F3EFE7] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#2B7A6B] transition-[width] duration-300"
                  style={{ width: `${barPct}%` }}
                />
              </div>
              <p className="text-xs text-[#0B1F3A]/55 mt-1">{m.lines} lines · {m.units} units</p>
            </div>
          );
        })}
      </Section>
    </section>
  );
}

function KPI({ icon, label, value, accent = "text-[#0B1F3A]", testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E6E0D4] rounded-xl p-4">
      <div className="flex items-center gap-2 text-[#0B1F3A]/65">
        {icon}
        <p className="text-[10.5px] tracking-eyebrow uppercase">{label}</p>
      </div>
      <p className={`font-serif-display text-3xl mt-2 ${accent}`}>{value}</p>
    </div>
  );
}

function Section({ title, count, empty, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-serif-display text-xl text-[#0B1F3A]">{title}</h3>
        <span className="text-xs text-[#0B1F3A]/55">{count}</span>
      </div>
      <div className="bg-white border border-[#E6E0D4] rounded-xl divide-y divide-[#EEE8DC]">
        {count === 0 ? (
          <div className="px-4 md:px-5 py-6 text-center text-sm text-[#0B1F3A]/55">{empty}</div>
        ) : children}
      </div>
    </div>
  );
}

function Row({ testId, title, subtitle, value, sharePct }) {
  return (
    <div className="px-4 md:px-5 py-3 flex items-center gap-3" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0B1F3A] truncate">{title}</p>
        <p className="text-xs text-[#0B1F3A]/60 mt-0.5 truncate">{subtitle}</p>
      </div>
      <div className="w-24 shrink-0 hidden sm:block">
        <div className="h-1.5 w-full bg-[#F3EFE7] rounded-full overflow-hidden">
          <div className="h-full bg-[#0B1F3A] transition-[width] duration-300" style={{ width: `${sharePct}%` }} />
        </div>
        <p className="text-[10px] text-[#0B1F3A]/50 mt-1 text-right">{sharePct}%</p>
      </div>
      <p className="text-sm font-medium text-[#0B1F3A] tabular-nums w-24 text-right shrink-0">{value}</p>
    </div>
  );
}
